import React, { useState } from 'react';
import { Eye, Info, Grid, Layers, Download } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для визуализации расположения светильников в помещении
 */
const VisualizationSection = ({ results, formData }) => {
  const [showGrid, setShowGrid] = useState(true);
  const [showDistances, setShowDistances] = useState(false);
  const [showIlluminance, setShowIlluminance] = useState(true);
  
  if (!results || !results.lampPositions) return null;

  const getIlluminanceColor = (value) => {
    // Цветовая схема для отображения освещенности
    if (value < 100) return 'rgba(255, 0, 0, 0.1)';
    if (value < 200) return 'rgba(255, 165, 0, 0.15)';
    if (value < 300) return 'rgba(255, 255, 0, 0.2)';
    if (value < 500) return 'rgba(144, 238, 144, 0.25)';
    return 'rgba(0, 255, 127, 0.3)';
  };

  // Получение параметров помещения
  const roomLength = parseFloat(formData.roomLength);
  const roomWidth = parseFloat(formData.roomWidth);
  
  // Корректировка масштаба для визуализации
  const maxDimension = Math.max(roomLength, roomWidth);
  const scale = 500 / maxDimension;
  
  const scaledLength = roomLength * scale;
  const scaledWidth = roomWidth * scale;

  // Рассчитываем оптимальные размеры светильников на плане
  const lampRadius = Math.min(10, Math.max(5, Math.min(scaledLength, scaledWidth) / 30));

  // Создание градиента освещенности (упрощенного)
  const createIlluminanceGradient = () => {
    const gradientPoints = [];
    
    // Добавляем основные точки градиента для каждого светильника
    results.lampPositions.forEach(lamp => {
      const x = lamp.x * scale;
      const y = lamp.y * scale;
      
      // Создаем градиентную точку для каждого светильника
      gradientPoints.push({
        x, y,
        radius: Math.min(results.adjustedLampDistanceX, results.adjustedLampDistanceY) * scale * 0.8,
        color: getIlluminanceColor(results.actualIlluminance),
        illuminance: results.actualIlluminance
      });
    });
    
    return gradientPoints;
  };

  const illuminanceGradient = createIlluminanceGradient();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Eye className="w-5 h-5 mr-2 text-amber-500" />
          Визуализация расположения светильников
        </h3>

        {/* Кнопки управления отображением */}
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant={showGrid ? "primary" : "outline"}
            onClick={() => setShowGrid(!showGrid)}
            icon={<Grid className="w-4 h-4" />}
          >
            Сетка
          </Button>
          <Button
            size="sm"
            variant={showDistances ? "primary" : "outline"}
            onClick={() => setShowDistances(!showDistances)}
            icon={<Layers className="w-4 h-4" />}
          >
            Размеры
          </Button>
          <Button
            size="sm"
            variant={showIlluminance ? "primary" : "outline"}
            onClick={() => setShowIlluminance(!showIlluminance)}
            icon={<Eye className="w-4 h-4" />}
          >
            Освещённость
          </Button>
        </div>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-amber-500 dark:text-amber-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-700 dark:text-amber-300">
          <p>Схематичное изображение помещения с рекомендуемым расположением светильников. Используйте кнопки выше для изменения отображения.</p>
        </div>
      </div>

      {/* Визуализация помещения и светильников */}
      <div className="w-full overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
        <div className="relative" style={{ width: scaledLength, height: scaledWidth, minWidth: '300px', minHeight: '200px', maxWidth: '600px', maxHeight: '500px', margin: '0 auto' }}>
          {/* Фон помещения */}
          <div className="absolute inset-0 bg-gray-50 dark:bg-gray-700"></div>
          
          {/* Сетка */}
          {showGrid && (
            <div className="absolute inset-0">
              {/* Горизонтальные линии */}
              {Array.from({ length: Math.ceil(roomWidth) + 1 }).map((_, i) => (
                <div 
                  key={`h-${i}`} 
                  className="absolute bg-gray-200 dark:bg-gray-600" 
                  style={{ 
                    left: 0, 
                    top: (i * scale), 
                    width: '100%', 
                    height: '1px'
                  }}
                ></div>
              ))}
              
              {/* Вертикальные линии */}
              {Array.from({ length: Math.ceil(roomLength) + 1 }).map((_, i) => (
                <div 
                  key={`v-${i}`} 
                  className="absolute bg-gray-200 dark:bg-gray-600" 
                  style={{ 
                    top: 0, 
                    left: (i * scale), 
                    height: '100%', 
                    width: '1px'
                  }}
                ></div>
              ))}
            </div>
          )}

          {/* Градиент освещенности */}
          {showIlluminance && illuminanceGradient.map((point, i) => (
            <div 
              key={`gradient-${i}`}
              className="absolute rounded-full"
              style={{
                left: point.x - point.radius,
                top: point.y - point.radius,
                width: point.radius * 2,
                height: point.radius * 2,
                background: `radial-gradient(circle, ${point.color} 0%, rgba(255,255,255,0) 70%)`,
                mixBlendMode: 'multiply'
              }}
            ></div>
          ))}

          {/* Светильники */}
          {results.lampPositions.map((lamp, index) => (
            <div
              key={`lamp-${index}`}
              className="absolute bg-amber-400 dark:bg-amber-500 border-2 border-amber-500 dark:border-amber-600 rounded-full transition-all duration-200 shadow-md hover:shadow-lg"
              style={{
                left: lamp.x * scale - lampRadius,
                top: lamp.y * scale - lampRadius,
                width: lampRadius * 2,
                height: lampRadius * 2,
                transform: 'translate(-50%, -50%)',
                zIndex: 10
              }}
              title={`Светильник ${index + 1}`}
            ></div>
          ))}

          {/* Размеры и расстояния */}
          {showDistances && (
            <>
              {/* Размер помещения - длина */}
              <div className="absolute flex flex-col items-center"
                style={{ 
                  left: scaledLength / 2, 
                  top: scaledWidth + 25, 
                  transform: 'translateX(-50%)' 
                }}
              >
                <div className="h-5 border-l border-r border-b border-gray-400 dark:border-gray-300" 
                  style={{ width: scaledLength - 20 }}
                ></div>
                <span className="mt-1 text-xs text-gray-600 dark:text-gray-300">{formData.roomLength} м</span>
              </div>

              {/* Размер помещения - ширина */}
              <div className="absolute flex items-center"
                style={{ 
                  top: scaledWidth / 2, 
                  left: scaledLength + 25, 
                  transform: 'translateY(-50%)' 
                }}
              >
                <div className="w-5 border-t border-b border-r border-gray-400 dark:border-gray-300" 
                  style={{ height: scaledWidth - 20 }}
                ></div>
                <span className="ml-1 text-xs text-gray-600 dark:text-gray-300">{formData.roomWidth} м</span>
              </div>

              {/* Расстояние между светильниками */}
              {results.lampsInRow > 1 && (
                <div className="absolute flex flex-col items-center z-20"
                  style={{ 
                    left: (results.lampPositions[0].x + results.lampPositions[1].x) * scale / 2,
                    top: results.lampPositions[0].y * scale - 25, 
                    transform: 'translateX(-50%)' 
                  }}
                >
                  <div className="h-4 border-l border-r border-t border-amber-500" 
                    style={{ width: results.adjustedLampDistanceX * scale }}
                  ></div>
                  <span className="mt-1 text-xs text-amber-600 dark:text-amber-400">{results.adjustedLampDistanceX.toFixed(1)} м</span>
                </div>
              )}

              {/* Расстояние между рядами */}
              {results.lampsInColumn > 1 && (
                <div className="absolute flex items-center z-20"
                  style={{ 
                    top: (results.lampPositions[0].y + results.lampPositions[results.lampsInRow].y) * scale / 2,
                    left: results.lampPositions[0].x * scale - 25, 
                    transform: 'translateY(-50%)' 
                  }}
                >
                  <div className="w-4 border-t border-b border-l border-amber-500" 
                    style={{ height: results.adjustedLampDistanceY * scale }}
                  ></div>
                  <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">{results.adjustedLampDistanceY.toFixed(1)} м</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Легенда */}
      <div className="flex flex-wrap gap-4 text-sm text-gray-700 dark:text-gray-300">
        <div className="flex items-center">
          <div className="w-4 h-4 rounded-full bg-amber-400 border border-amber-500 mr-2"></div>
          <span>Светильник</span>
        </div>
        {showIlluminance && (
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full mr-2" style={{ background: getIlluminanceColor(results.actualIlluminance) }}></div>
            <span>Освещенность ~{Math.round(results.actualIlluminance)} лк</span>
          </div>
        )}
        <div className="ml-auto text-xs text-gray-500 dark:text-gray-400">
          Масштаб: примерно 1:{Math.round(maxDimension / 5)}
        </div>
      </div>
    </div>
  );
};

export default VisualizationSection;